import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import {
  COMMITTEE_PAGE_SIZE,
  COMMITTEE_PAGE_SIZES,
  COMMITTEE_SEARCH_DEBOUNCE_MS,
  COMMITTEE_STATUS_PILL,
  LEASE_STATUS_PILL,
  LeaseGroup,
  LeaseLens,
  PillDef,
} from '../../data/constants/committee.constants'
import { ApiCommitteeService } from '@data/services/modules/committee/api-committee.service'
import { CommitteeShellService } from '../../data/services/view/committee-shell.service'
import { CommitteeLeaseRow } from '../../data/interfaces/committee-session.interface'

/**
 * Los arrendamientos de un grupo con su estado de comité: Por originar, Activos o Cerrados.
 *
 * Es UNA vista para tres rutas; qué grupo enseña sale del `data.group` de su ruta y no de un `@Input`:
 * desde que Comités es un cascarón con `<router-outlet>`, a este componente lo crea el enrutador.
 *
 * **Lo que se hace aquí es elegir y llevar.** Se marcan filas y se abre el modal de nueva sesión con
 * ellas (`?llevar=id,id`); el tipo de sesión lo decide el modal por el grupo de origen. Las filas
 * elegidas se dejan en el cascarón para que el modal pinte folio y cliente sin volver a pedirlos.
 *
 * **La lente «Sin comité» la aplica el servidor.** Filtrar en el cliente la página que se está viendo
 * daría un número falso: la lente cuenta toda la cartera del grupo, y ese conteo se pide aparte.
 */
@Component({
  selector: 'app-committee-leases',
  templateUrl: './committee-leases.component.html',
  styleUrls: ['./committee-leases.component.scss'],
})
export class CommitteeLeasesComponent implements OnInit, OnDestroy {
  group: LeaseGroup = 'originating'
  lens: LeaseLens = 'all'

  rows: CommitteeLeaseRow[] = []
  total = 0
  loading = true
  error = ''

  page = 0
  pageSize = COMMITTEE_PAGE_SIZE
  readonly pageSizeOptions = COMMITTEE_PAGE_SIZES

  query = ''
  /** Cuántos hay sin comité en TODO el grupo, no en esta página. `null` mientras no se sabe. */
  withoutCommitteeTotal: number | null = null

  /** Qué filas están marcadas, por id. */
  selected = new Set<string>()

  readonly skeleton = [1, 2, 3, 4, 5, 6, 7, 8]
  readonly leasePill: Partial<Record<string, PillDef>> = LEASE_STATUS_PILL
  readonly committeePill: Partial<Record<string, PillDef>> = COMMITTEE_STATUS_PILL

  private readonly search$ = new Subject<string>()
  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly api: ApiCommitteeService,
    private readonly shell: CommitteeShellService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    //* Se suscribe a `data` y no al snapshot: si el enrutador reutiliza la instancia entre dos rutas
    //* hermanas, el grupo cambia sin que este componente se recree.
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe((d) => {
      this.group = (d['group'] as LeaseGroup) ?? 'originating'
      this.lens = 'all'
      this.query = ''
      this.page = 0
      this.selected.clear()
      this.load()
      this.countWithoutCommittee()
    })

    //* Se espera a que deje de teclear: sin esto son ocho consultas para escribir «ARNOLD».
    this.search$.pipe(debounceTime(COMMITTEE_SEARCH_DEBOUNCE_MS), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(() => {
      this.page = 0
      this.load()
    })

    //* Se creó una sesión con filas de esta tabla: su estado de comité cambió y hay que volver a pedir.
    this.shell.refresh$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.selected.clear()
      this.load()
      this.countWithoutCommittee()
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  /** En Por originar la pregunta no es si tiene comité —ninguno decidió— sino si ya está en una sesión. */
  get lensLabel(): string {
    return this.group === 'originating' ? 'Sin sesión' : 'Sin comité'
  }

  /** Por originar abre una VOTACIÓN; Activos y Cerrados registran un acta histórica (D-2). */
  get actionLabel(): string {
    return this.group === 'originating' ? 'Llevar a sesión' : 'Registrar acta'
  }

  get selectedRows(): CommitteeLeaseRow[] {
    return this.rows.filter((r) => this.selected.has(r._id))
  }

  /** Las filas que SE PUEDEN llevar: un arrendamiento con reunión abierta ya está en una sesión (409 si se intenta). */
  get selectable(): CommitteeLeaseRow[] {
    return this.rows.filter((r) => this.canSelect(r))
  }

  get allSelected(): boolean {
    const rows = this.selectable

    return rows.length > 0 && rows.every((r) => this.selected.has(r._id))
  }

  canSelect(row: CommitteeLeaseRow): boolean {
    return !row.hasOpenMeeting
  }

  isSelected(row: CommitteeLeaseRow): boolean {
    return this.selected.has(row._id)
  }

  toggle(row: CommitteeLeaseRow): void {
    if (!this.canSelect(row)) return
    if (this.selected.has(row._id)) this.selected.delete(row._id)
    else this.selected.add(row._id)
  }

  /** Marca o desmarca la PÁGINA visible. La selección no cruza páginas: lo que no se ve no se lleva. */
  toggleAll(): void {
    if (this.allSelected) this.selectable.forEach((r) => this.selected.delete(r._id))
    else this.selectable.forEach((r) => this.selected.add(r._id))
  }

  onSearch(value: string): void {
    this.query = value
    this.search$.next(value)
  }

  clearSearch(): void {
    if (!this.query) return
    this.onSearch('')
  }

  /**
   * Enciende o apaga la lente. Al cambiarla se vuelve a la primera página: dejar la tres puesta
   * sobre un listado que ahora tiene dos filas enseña una tabla vacía sin explicar por qué.
   */
  toggleLens(): void {
    this.lens = this.lens === 'all' ? 'without-committee' : 'all'
    this.page = 0
    this.load()
  }

  //* `PaginatorState` de PrimeNG declara `first`/`rows` como opcionales, así que se leen con respaldo.
  onPage(e: { first?: number; rows?: number }): void {
    this.pageSize = e.rows ?? this.pageSize
    this.page = this.pageSize ? Math.floor((e.first ?? 0) / this.pageSize) : 0
    this.load()
  }

  /**
   * Abre el modal de nueva sesión con lo marcado, POR LA DIRECCIÓN (`?llevar=id,id`).
   *
   * Las filas se dejan en el cascarón antes de navegar: el modal solo recibe ids y con ellas pinta el
   * folio y el cliente de cada chip sin volver a pedirlos.
   */
  bring(): void {
    const rows = this.selectedRows
    if (!rows.length) return

    this.shell.stage(rows)
    this.router.navigate([], { relativeTo: this.route, queryParams: { llevar: rows.map((r) => r._id).join(',') }, queryParamsHandling: 'merge' })
  }

  private load(): void {
    this.loading = true
    this.error = ''
    this.api.getLeases(this.group, this.lens, this.page + 1, this.pageSize, this.query).subscribe((r) => {
      this.loading = false
      if (r.error) {
        this.rows = []
        this.total = 0
        this.error = r.msg || 'No se pudieron cargar los arrendamientos.'
        return
      }
      this.rows = r.data?.data ?? []
      this.total = r.data?.totalItems ?? 0
    })
  }

  /**
   * El número que va en la lente. Se pide aparte, con `limit: 1`, porque tiene que contar TODO el
   * grupo y no la página que se está viendo. No depende de la búsqueda: es la cifra de la cartera.
   */
  private countWithoutCommittee(): void {
    this.api.getLeases(this.group, 'without-committee', 1, 1).subscribe((r) => {
      this.withoutCommitteeTotal = r.error ? null : (r.data?.totalItems ?? 0)
    })
  }
}
